import { useEffect, useState } from 'react'
import { Box, Grid, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import { CustomInput, CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import PrescriptionSectionCard from './PrescriptionSectionCard'
import { FOOTER_TYPES_ARRAY } from 'src/consumer/constants'
import { getEventTargetValueFromMuiToggleGroup } from 'src/consumer/helpers/utilFunctions'
import { getNumberStringForInput } from 'src/mentor/helpers/utilityFunctions'
import { getClinicDetailsColumnCount } from '../utils/prescriptionLayoutConfig'
import FooterFieldsRenderer from './FooterFieldsRenderer'
import RelatedClinicDetails from './RelatedClinicDetails'
import ImageUploadWithHeightAdjustment from '../ImageUploadWithHeightAdjustment'
import TemplatePlacementHelpDialog from './TemplatePlacementHelpDialog'

const FOOTER_CONTENT_MODES = {
  builtIn: 'builtIn',
  image: 'image',
}

/**
 * Section for footer configuration
 * Handles footer height, type, blank/filled state, footer image, related clinics, and footer content fields
 */
export default function FooterDetailsSection({
  prescriptionTemplate,
  handleChangeFooter,
  handleChangeRelatedClinicDetails,
  disabled,
  isUsingNewPrintMethod = false,
}) {
  const layout = prescriptionTemplate?.layout
  const columnCount = getClinicDetailsColumnCount(layout)
  const hasFooterImage = Boolean(prescriptionTemplate?.footer?.imageId)
  const [footerContentMode, setFooterContentMode] = useState(
    hasFooterImage ? FOOTER_CONTENT_MODES.image : FOOTER_CONTENT_MODES.builtIn,
  )

  useEffect(() => {
    if (hasFooterImage) {
      setFooterContentMode(FOOTER_CONTENT_MODES.image)
    }
  }, [hasFooterImage])

  const isImageFooter = footerContentMode === FOOTER_CONTENT_MODES.image

  const handleFooterContentModeChange = (_, nextMode) => {
    if (!nextMode || disabled) {
      return
    }

    setFooterContentMode(nextMode)
    if (nextMode === FOOTER_CONTENT_MODES.builtIn && hasFooterImage) {
      handleChangeFooter({ target: { id: 'imageId', value: null } })
    }
  }

  return (
    <PrescriptionSectionCard heading="Footer Details">
      <Grid item xs={12}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'flex-end' }}
          spacing={1.5}
        >
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <CustomSelect
              id="customFooterType"
              value={prescriptionTemplate?.footer?.customFooterType}
              options={FOOTER_TYPES_ARRAY}
              onChangeEvent={handleChangeFooter}
              label="Footer Type"
              disabled={disabled}
              isClearable
              getOptionValue={(option) => option?.id}
              getOptionLabel={(option) => option?.title}
              selectType={selectTypes.string}
            />
          </Box>
          <TemplatePlacementHelpDialog
            label="Footer Type"
            kind="footer"
            options={FOOTER_TYPES_ARRAY}
          />
        </Stack>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Footer source
        </Typography>
        <ToggleButtonGroup
          color="primary"
          size="small"
          value={footerContentMode}
          exclusive
          onChange={handleFooterContentModeChange}
          disabled={disabled}
          sx={{
            flexWrap: 'wrap',
            '& .MuiToggleButtonGroup-grouped': { borderRadius: '8px !important' },
            '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.75 },
          }}
        >
          <ToggleButton value={FOOTER_CONTENT_MODES.builtIn}>
            <TextFieldsIcon fontSize="small" sx={{ mr: 0.75 }} />
            Built-in footer
          </ToggleButton>
          <ToggleButton value={FOOTER_CONTENT_MODES.image}>
            <ImageOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
            Image footer
          </ToggleButton>
        </ToggleButtonGroup>
      </Grid>

      {isImageFooter && (
        <Grid item xs={12} md={12}>
          <ImageUploadWithHeightAdjustment
            imageId={prescriptionTemplate?.footer?.imageId}
            currentHeight={prescriptionTemplate?.footer?.customFooterHeight}
            prescriptionTemplate={prescriptionTemplate}
            type="footer"
            defaultHeight={2}
            minHeight={0.5}
            maxHeight={5}
            showSlider={isUsingNewPrintMethod}
            onComplete={(imageId, adjustedHeight) => {
              handleChangeFooter({ target: { id: 'imageId', value: imageId } })
              handleChangeFooter({ target: { id: 'customFooterHeight', value: adjustedHeight } })
            }}
            disabled={disabled}
            placeholder="Add Footer Image"
          />
        </Grid>
      )}

      <Grid item xs={12} md={12}>
        <CustomInput
          id="customFooterHeight"
          label="Custom footer height in inches (manual adjustment)"
          type="number"
          value={getNumberStringForInput(prescriptionTemplate?.footer?.customFooterHeight)}
          onChange={handleChangeFooter}
          disabled={disabled}
        />
      </Grid>

      {!isImageFooter && (
        <>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Footer content
            </Typography>
            <ToggleButtonGroup
              color="primary"
              id="customFooterBlank"
              size="small"
              value={prescriptionTemplate?.footer?.customFooterBlank}
              exclusive
              onChange={(e, v) =>
                handleChangeFooter(getEventTargetValueFromMuiToggleGroup(false, e, v))
              }
              sx={{
                flexWrap: 'wrap',
                '& .MuiToggleButtonGroup-grouped': { borderRadius: '8px !important' },
                '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.75 },
              }}
            >
              <ToggleButton id="customFooterBlank" value={true}>
                Show Blank Footer
              </ToggleButton>
              <ToggleButton id="customFooterBlank" value={false}>
                Show Filled Footer
              </ToggleButton>
              <ToggleButton id="customFooterBlank" value={null}>
                Use Default Values
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {/* Related clinic details for multi-column footers */}
          {columnCount > 0 &&
            Array.from({ length: columnCount }).map((_, idx) => (
              <RelatedClinicDetails
                key={idx}
                clinicDetails={prescriptionTemplate?.relatedClinicDetails?.[idx]}
                idx={idx}
                layoutType={layout}
                handleChange={(e) => handleChangeRelatedClinicDetails(e, idx)}
              />
            ))}

          {/* Dynamic footer fields based on layout */}
          <FooterFieldsRenderer
            prescriptionTemplate={prescriptionTemplate}
            handleChangeFooter={handleChangeFooter}
            disabled={disabled}
          />
        </>
      )}
    </PrescriptionSectionCard>
  )
}
